'use client'

import * as React from 'react'

import { cn } from '../lib/utils.js'

/** One in-page destination. `id` must match the `id` of an element on the page. */
type OnThisPageItem = {
  /** `id` of the section this entry points at. */
  id: string
  /** Visible link text. */
  title: React.ReactNode
}

type OnThisPageProps = Omit<React.ComponentPropsWithoutRef<'nav'>, 'children'> & {
  /** In-page destinations, in document order. */
  items: OnThisPageItem[]
  /**
   * `horizontal` is the sticky bar that docks under the site chrome;
   * `vertical` is the left- or right-hand rail beside long-form content.
   *
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * Y-coordinate, in CSS pixels from the top of the viewport, of the line a
   * section must cross to count as the one being read. Set it to the height of
   * whatever chrome overlays the top of the page (plus any slack you want),
   * or sections scrolled to via their anchor register as active a fraction too
   * late. `useChromeHeight` measures that number for you.
   *
   * @default 0
   */
  offset?: number
  /**
   * Active section (controlled). When set, the component reports what it
   * observes through `onActiveChange` but never sets its own state — pick one
   * mode per instance and stay in it, as with `ThemeSwitcher`.
   */
  activeId?: string | null
  /** Fired whenever the observed section changes, in both modes. */
  onActiveChange?: (id: string | null) => void
  /** Shown instead of the list when `items` is empty. `null` renders nothing. */
  emptyMessage?: React.ReactNode
  ref?: React.Ref<HTMLElement>
}

/**
 * In-page navigation that tracks which section the reader has reached.
 *
 * **Not to be confused with the other three navigation components**, which
 * this deliberately does not overlap:
 *
 * | Component        | Moves you                        |
 * | ---------------- | -------------------------------- |
 * | `MainNav`        | between sites/sections, mega menu |
 * | `SideNav`        | between pages, as a tree          |
 * | `StepNav`        | through a multi-page journey      |
 * | `OnThisPage`     | within the page you are on        |
 *
 * It is named `OnThisPage` rather than the more obvious `SectionNav` because
 * `SideNav` already announces itself as "Section navigation" by default, and
 * two components with near-identical names and different jobs is the kind of
 * ambiguity a design system exists to remove.
 *
 * **No Base UI primitive is involved, and none is missing.** The house rule is
 * never to hand-roll ARIA, focus or keyboard behaviour — but these are plain
 * anchors, which already have all three natively. What is added here is
 * position tracking, which is not an interaction pattern.
 *
 * Accessibility contract:
 *
 * - The landmark is named (`aria-label`, default "On this page") so it is
 *   distinguishable from the page's other navigation landmarks (WCAG 2.2,
 *   1.3.1).
 * - The tracked entry is marked **`aria-current="location"`, not `"page"`**.
 *   The reader has not navigated anywhere — every entry points at the page
 *   they are already on — so `"page"` would announce every link as current.
 *   `"location"` is the value defined for "the closest ancestor in a set of
 *   related elements", which is exactly a position within a document.
 * - `role="list"` is explicit on the `<ul>`: `list-style: none` strips list
 *   semantics in Safari/VoiceOver, the same reason `SideNav` and
 *   `StepIndicator` set it.
 * - The active state changes colour **and** the marker rule, so it is not
 *   signalled by colour alone (WCAG 2.2, 1.4.1). The rule is always drawn, in
 *   `transparent` when idle, so becoming active never changes an element's
 *   size.
 *
 * Items are resolved by `document.getElementById` after mount. Sections that
 * appear later (async content, a client-only region) are not picked up until
 * `items` changes — pass a new array when the set of sections changes.
 */
function OnThisPage({
  className,
  items,
  orientation = 'horizontal',
  offset = 0,
  activeId,
  onActiveChange,
  emptyMessage = null,
  'aria-label': ariaLabel = 'On this page',
  ref,
  ...props
}: OnThisPageProps) {
  const isControlled = activeId !== undefined
  const [observed, setObserved] = React.useState<string | null>(null)

  // Kept in refs so the scroll effect never needs them in its dependency list:
  // re-subscribing scroll/resize listeners because a parent re-rendered with a
  // new inline callback would drop the current measurement on every keystroke
  // in an unrelated part of the page.
  const onActiveChangeRef = React.useRef(onActiveChange)
  React.useEffect(() => {
    onActiveChangeRef.current = onActiveChange
  }, [onActiveChange])

  const lastReported = React.useRef<string | null>(null)

  // `items` is an array, so a caller building it inline gives a new identity
  // every render. Depending on the ids alone keeps the effect stable for the
  // overwhelmingly common case of a constant list.
  const ids = items.map((item) => item.id).join(' ')

  React.useEffect(() => {
    const sectionIds = ids ? ids.split(' ') : []
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    if (sections.length === 0) {
      return
    }

    let frame = 0

    function update() {
      frame = 0

      // The last section can be shorter than the remaining viewport, so it
      // would never cross the line on its own and could never become active.
      // At the bottom of the page it is unambiguously the one being read.
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2

      const current = atBottom
        ? (sections[sections.length - 1]?.id ?? null)
        : sections.reduce<string | null>(
            (found, element) =>
              element.getBoundingClientRect().top <= offset ? element.id : found,
            null,
          )

      if (current === lastReported.current) {
        return
      }
      lastReported.current = current

      if (!isControlled) {
        setObserved(current)
      }
      onActiveChangeRef.current?.(current)
    }

    function onScroll() {
      // Coalesce to one measurement per frame — scroll fires far more often
      // than the page can paint, and each measurement forces layout.
      if (frame === 0) {
        frame = requestAnimationFrame(update)
      }
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame)
      }
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [ids, offset, isControlled])

  const active = isControlled ? activeId : observed

  return (
    <nav
      ref={ref}
      data-slot='on-this-page'
      data-orientation={orientation}
      aria-label={ariaLabel}
      className={cn(
        'text-base',
        // The scroll container for the horizontal bar — `min-w-max` on the list
        // below only does anything with an overflowing parent. A visible
        // scrollbar would sit on top of the links, so it is suppressed here, on
        // the element that actually scrolls; the clipped final item is what
        // signals there is more to the right. Scrolling itself is untouched, so
        // pointer drag, shift-wheel and keyboard all still reach the overflow.
        orientation === 'horizontal' &&
          '[scrollbar-width:none] overflow-x-auto [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    >
      {items.length === 0 ? (
        emptyMessage == null ? null : (
          <p data-slot='on-this-page-empty' className='text-muted-foreground'>
            {emptyMessage}
          </p>
        )
      ) : (
        <ul
          role='list'
          data-slot='on-this-page-list'
          className={cn(
            'flex',
            orientation === 'horizontal'
              ? 'min-w-max items-center'
              : 'flex-col border-s border-border',
          )}
        >
          {items.map((item) => {
            const isActive = active === item.id
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  aria-current={isActive ? 'location' : undefined}
                  data-slot='on-this-page-link'
                  data-active={isActive ? '' : undefined}
                  className={cn(
                    'flex font-bold whitespace-nowrap',
                    'hover:bg-primary/10 hover:text-primary',
                    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                    'motion-safe:transition-colors',
                    orientation === 'horizontal'
                      ? 'items-center border-b-2 p-4'
                      : '-ms-px items-baseline border-s-2 px-4 py-2',
                    // The idle ink lives in this branch rather than the base
                    // string: both colours are single-class selectors, so with
                    // them on the element at once the winner would come down to
                    // Tailwind's internal sort order.
                    isActive ? 'border-primary text-primary' : 'border-transparent text-foreground',
                  )}
                >
                  {item.title}
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </nav>
  )
}

export { OnThisPage }
export type { OnThisPageItem, OnThisPageProps }
